// @migration-in-progress
import { devLog } from '../utils/devLog';
/**
 * Outbox pattern implementation for offline-first sync
 * Queues all changes for future synchronization with cloud
 * 
 * CRITICAL: This enables reliable sync even with intermittent connectivity
 */

import { readJSON, writeJSON } from './storageRouter';
import { genId, genTimestamp } from './id';
import { ARRAY_COLLECTIONS } from '../sync/moduleMap';
import { azureApiClient } from './azureApiClient';
// FT-331 amend (2026-07-07): use the SHARED hoisted extractItems (was inline copy).
// The prior inline copy shared the FT-324 ghost-doc-poisoning bug class and was
// missed by the initial FT-331 patch — outbox's processOutbox pull-merge-push path
// now benefits from the same belt-and-braces wrapper-rejection guard as
// UniversalSyncBridge. Extracted to its own file (src/sync/extractItems.js) to
// avoid a circular import: UniversalSyncBridge already imports pushOutbox/
// processOutbox from this module.
import { extractItems } from '../sync/extractItems';
// FT-446: shared safe PUSH tie-break. Leaf module (no bridge import) so this
// does NOT recreate the UniversalSyncBridge → outbox → UniversalSyncBridge
// cycle (bridge imports processOutbox from here). Before this, the outbox
// drain did an UNCONDITIONAL `uniqueMap.set(i.id, i)` for every local item —
// no timestamp check at all — so a stale queued item overwrote a NEWER cloud
// copy. That is the same cross-device sign-off clobber as the bridge, worse.
import { resolvePushMergeWinner } from '../sync/resolvePushMergeWinner';
// FT-458: sticky soft-delete retain — same leaf used by the bridge pull + push
// merges. Ordered AFTER the FT-446 tie-break so it only ADDS the tombstone
// marker onto the chosen winner (never re-decides the record body). Without it
// the offline-drain path resurrects a locally-deleted item just like the bridge
// push did.
import { retainTombstone } from '../sync/tombstone';
// FT-565: config-driven list-field union (equipment photos + auditLog). Leaf
// module (only imports the ./tombstone leaf) — no FT-331 cycle. Same function
// the bridge pull + push merges use.
import { mergeListFields } from '../sync/mergeListFields';

export const OUTBOX_KEY = 'sr.outbox.v1'; // FT-577: exported for lib/outboxFailures.js

// ---------------------------------------------------------------------------
// FT-445 — OUTBOX WRITE SERIALISATION (silent-data-loss fix)
//
// Every mutator below performs an ASYNC read-modify-write on the single shared
// key OUTBOX_KEY: `const o = await listOutbox(); o.push(...); await writeJSON(o)`.
// On the shipped flags (USE_INDEXEDDB:true, PARALLEL_STORAGE:false) BOTH the
// read and the write are async IndexedDB ops, so each `await` yields the event
// loop. Two concurrent callers therefore both read the SAME base array, each
// appends only its own change, and the last writer clobbers the earlier one —
// a classic lost update. Observed 3-of-4 concurrent enqueues dropped, 5/5 runs
// on real staging. NET EFFECT: a photo blob is saved locally but its
// upload-note is silently dropped → the photo never reaches the server.
//
// FIX: funnel EVERY read-modify-write of OUTBOX_KEY through a single
// module-level promise chain so the critical sections run ONE AT A TIME (each
// re-reads inside the lock, mutates, writes, then releases). This preserves
// FT-234 durability, FIFO append order (calls line up in arrival order),
// FT-365 attribution and FT-379 abort-drop (those live outside outbox writes).
//
// HAZARDS EXPLICITLY CLOSED:
//   • No stall on rejection — the chain advances on BOTH resolve and reject
//     (`.then(run, run)`), and the tail is swallowed so a rejected write can
//     never poison the queue or leave an unhandled rejection.
//   • No deadlock / re-entrancy — processOutbox is deliberately NOT wrapped
//     (it does long network I/O); it calls the locked helpers markOutboxFailed
//     / clearOutboxItems, each of which acquires the lock briefly and
//     independently. There is no nested lock acquisition.
//   • Bounded — the chain holds exactly ONE pending Promise, not a growing list.
//   • Backend-agnostic — a plain promise chain, NOT a Dexie transaction, so it
//     does not tangle with the storageRouter / feature-flag layering.
// ---------------------------------------------------------------------------
let outboxWriteChain = Promise.resolve();

/**
 * Serialise one outbox read-modify-write critical section. Callers that mutate
 * OUTBOX_KEY MUST run their whole read+mutate+write inside `task` so no two
 * mutations interleave. Returns the task's own result/rejection to the caller,
 * while keeping the shared chain alive regardless of that outcome.
 * @template T
 * @param {() => Promise<T>} task
 * @returns {Promise<T>}
 */
export const withOutboxLock = (task) => {
  // The value each caller awaits: this specific task's real settlement.
  const result = outboxWriteChain.then(task, task);
  // Advance the shared chain on BOTH resolve and reject so a rejecting write
  // never wedges the queue; swallow the tail so there is no unhandled rejection
  // and the next caller starts from a clean resolved promise.
  outboxWriteChain = result.then(() => undefined, () => undefined);
  return result;
};

/**
 * Change types for tracking
 */
export const CHANGE_TYPES = {
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  BULK: 'bulk'
};

/**
 * FT-577: an item is retried automatically (on app start / reconnect) until it
 * has failed this many times; after that it is PERMANENTLY failed and only a
 * manual "Try again" (retryAllFailedItems) or the photo reconcile revives it.
 */
export const MAX_OUTBOX_RETRIES = 3;

/** FT-577: deliberate safety block (would have wiped cloud data) — not lost data. */
export const FAILURE_KIND_BLOCKED_EMPTY = 'blocked-empty';

/** FT-577: event the header sync badge listens to for prompt refresh. */
export const OUTBOX_STATUS_EVENT = 'outboxStatusChanged';

export const notifyOutboxStatus = (detail = {}) => {
  try {
    window.dispatchEvent(new CustomEvent(OUTBOX_STATUS_EVENT, { detail }));
  } catch { /* non-browser env */ }
};

/**
 * Push a change to the outbox queue
 * @param {Object} change - The change to queue
 * @param {string} change.collection - Collection name (e.g., 'sr.policies.v1')
 * @param {string} change.type - Change type (create, update, delete)
 * @param {Object} change.data - The actual data
 * @param {string} change.recordId - ID of the affected record
 */
export const pushOutbox = async (change) => {
  try {
    // FT-365 v2: attribution snapshot at CREATION time. Used at flush to
    // reject cross-user pushes (User A's queued write must never be pushed
    // under User B's JWT). Regulatory-critical for ISM audit trail (§6).
    // sync-only getCurrentUser() — no network, no getSession() side-effects.
    let sessionSnapshot = null;
    try {
      const currentUser = azureApiClient.getCurrentUser
        ? azureApiClient.getCurrentUser()
        : null;
      if (currentUser) {
        sessionSnapshot = {
          userId: currentUser.id || null,
          orgId: currentUser.organizationId || null,
          role: currentUser.role || null,
          snapshotAt: Date.now(),
        };
      }
    } catch {
      // Non-fatal — legacy behaviour is to write without a snapshot; the
      // flush-time check treats missing snapshot as "no attribution info"
      // and pushes normally (backwards compatible).
    }

    const entry = {
      id: genId(),
      timestamp: genTimestamp(),
      collection: change.collection,
      type: change.type,
      recordId: change.recordId,
      data: change.data,
      vesselId: change.vesselId || await getCurrentVesselId(),
      status: 'pending',
      retryCount: 0,
      error: null,
      session: sessionSnapshot // FT-365 v2 — may be null on legacy/unauthenticated writes
    };

    // FT-445: the read-modify-write MUST be serialised. Two concurrent
    // pushOutbox calls previously both read the same array and clobbered each
    // other (lost update). We re-read INSIDE the lock so we always append to
    // the freshest array, and return the post-append length as the truthful
    // event count.
    const count = await withOutboxLock(async () => {
      const outbox = await listOutbox();
      outbox.push(entry);
      await writeJSON(OUTBOX_KEY, outbox);
      return outbox.length;
    });

    // Emit event for potential listeners. Kept OUTSIDE the lock (it performs no
    // outbox write) so listeners never run inside the critical section.
    window.dispatchEvent(new CustomEvent('outboxChanged', {
      detail: { entry, count }
    }));

    return entry.id;
  } catch (error) {

    return null;
  }
};

/**
 * List all pending changes in the outbox
 */
export const listOutbox = async () => {
  return await readJSON(OUTBOX_KEY, []);
};

/**
 * Get pending changes count
 */
export const getOutboxCount = async () => {
  const outbox = await listOutbox();
  return outbox.filter(item => item.status === 'pending').length;
};

/**
 * Clear successfully synced items from outbox
 * @param {Array<string>} ids - IDs of items to clear
 */
export const clearOutboxItems = async (ids) => {
  try {
    // FT-445: serialise. Re-reads INSIDE the lock so a note enqueued DURING a
    // long processOutbox flush is preserved — we only strip the given ids from
    // the CURRENT array, never from a stale pre-flush snapshot.
    await withOutboxLock(async () => {
      const outbox = await listOutbox();
      const remaining = outbox.filter(item => !ids.includes(item.id));
      await writeJSON(OUTBOX_KEY, remaining);
    });
    notifyOutboxStatus({ status: 'cleared', count: ids.length });
    return true;
  } catch (error) {

    return false;
  }
};

/**
 * Clear entire outbox (use with caution)
 */
export const clearOutbox = async () => {
  try {
    // FT-445: serialise the bare write so it cannot interleave with a
    // concurrent enqueue's read-modify-write.
    await withOutboxLock(async () => {
      await writeJSON(OUTBOX_KEY, []);
    });
    window.dispatchEvent(new CustomEvent('outboxCleared'));
    return true;
  } catch (error) {

    return false;
  }
};

/**
 * Mark an outbox item as failed
 * @param {string} id
 * @param {string} error  human-readable reason
 * @param {{ status?: number, kind?: string }} [meta]  FT-577: HTTP status + failure kind
 */
export const markOutboxFailed = async (id, error, meta = {}) => {
  try {
    // FT-445: serialise. Re-reads inside the lock so marking one item failed
    // during a flush cannot clobber a concurrently-enqueued note.
    await withOutboxLock(async () => {
      const outbox = await listOutbox();
      const item = outbox.find(i => i.id === id);

      if (item) {
        item.status = 'failed';
        item.error = error;
        item.retryCount = (item.retryCount || 0) + 1;
        item.lastRetry = genTimestamp();
        item.lastStatus = typeof meta.status === 'number' ? meta.status : null;
        item.failureKind = meta.kind || null;
        await writeJSON(OUTBOX_KEY, outbox);
      }
    });
    notifyOutboxStatus({ id, status: 'failed' });

    return true;
  } catch (error) {

    return false;
  }
};

/**
 * Retry failed items
 */
export const retryFailedItems = async () => {
  try {
    // FT-445: serialise the whole read-modify-write.
    return await withOutboxLock(async () => {
      const outbox = await listOutbox();
      let updated = false;

      outbox.forEach(item => {
        if (item.status === 'failed' && item.retryCount < MAX_OUTBOX_RETRIES) {
          item.status = 'pending';
          item.error = null;
          updated = true;
        }
      });

      if (updated) {
        await writeJSON(OUTBOX_KEY, outbox);
      }

      return updated;
    });
  } catch (error) {

    return false;
  }
};

/**
 * Process outbox - Push pending changes to cloud
 * CRITICAL: Wraps arrays to prevent corruption
 * @param {Object} cloudClient - Supabase cloud client with push() method
 * @returns {Object} { success: number, failed: number, offline: boolean }
 */
export const processOutbox = async (cloudClient) => {
  // MANDATORY: Check offline status first
  if (!navigator.onLine) {
    devLog('[OUTBOX] Cannot process while offline');
    return { success: 0, failed: 0, offline: true };
  }

  try {
    const outbox = await listOutbox();
    const pending = outbox.filter(item => item.status === 'pending');

    if (pending.length === 0) {
      devLog('[OUTBOX] No pending items to sync');
      return { success: 0, failed: 0, offline: false };
    }

    devLog(`[OUTBOX] Processing ${pending.length} pending items`);

    // FT-365 v2 attribution check — reject cross-user pushes at flush time.
    // Items whose session snapshot userId does NOT match the current
    // authenticated user are HELD (left in the outbox, not pushed) so they
    // can flush later when the original user re-authenticates. Regulatory:
    // stops User A's queued defect syncing under User B's JWT (§6).
    // Legacy items with no `session` field push normally (backwards compat).
    let currentUserId = null;
    try {
      const currentUser = azureApiClient.getCurrentUser
        ? azureApiClient.getCurrentUser()
        : null;
      currentUserId = currentUser?.id || null;
    } catch {
      // No current user — treat like legacy path (push everything).
    }

    const eligible = [];
    for (const item of pending) {
      const originalUserId = item?.session?.userId || null;
      if (originalUserId && currentUserId && originalUserId !== currentUserId) {
        devLog('[OUTBOX] FT-365 attribution mismatch — HOLDING item', {
          entryId: item.id,
          collection: item.collection,
          originalUserId,
          currentUserId,
        });
        try {
          window.dispatchEvent(new CustomEvent('sr:outbox:attribution-mismatch', {
            detail: {
              entryId: item.id,
              collection: item.collection,
              originalUserId,
              currentUserId,
            }
          }));
        } catch { /* non-fatal */ }
        continue; // do NOT push this item — leave status=pending, retry when
                  // the original user is next authenticated on this device.
      }
      eligible.push(item);
    }

    if (eligible.length === 0) {
      devLog('[OUTBOX] All pending items filtered by FT-365 attribution guard');
      return { success: 0, failed: 0, offline: false };
    }

    // Group by collection for batch operations
    const batches = {};
    eligible.forEach(item => {
      if (!batches[item.collection]) {
        batches[item.collection] = [];
      }
      batches[item.collection].push(item);
    });

    let successCount = 0;
    let failedCount = 0;
    const successIds = [];

    // Process each collection batch
    for (const [collection, items] of Object.entries(batches)) {
      devLog(`[OUTBOX] Syncing ${items.length} items to ${collection}`);

      for (const item of items) {
        try {
          // CRITICAL: Wrap arrays to prevent corruption
          // This follows UniversalSyncBridge.js lines 115-129 pattern
          const syncData = Array.isArray(item.data)
            ? {
                items: item.data,           // Wrap array
                _isArray: true,             // Flag for unwrapping
                vesselId: item.vesselId,
                updatedAt: item.timestamp,
                syncedAt: Date.now()
              }
            : {
                ...item.data,               // Spread object (safe)
                vesselId: item.vesselId,
                updatedAt: item.timestamp,
                syncedAt: Date.now()
              };

          // 🛡️ OUTBOX PUSH GUARD: Match UniversalSyncBridge cloud-aware protection (2026-03-05)
          // Issue A fix: outbox.js was bypassing all empty-push guards
          const isEmptyArray = syncData._isArray && Array.isArray(syncData.items) && syncData.items.length === 0;
          const isEmptyObject = !syncData._isArray && typeof syncData === 'object' &&
            Object.keys(syncData).filter(k => !['vesselId', 'updatedAt', 'syncedAt', '_isArray', 'items'].includes(k)).length === 0;

          if (ARRAY_COLLECTIONS.has(collection) && (isEmptyArray || isEmptyObject)) {
            // Check cloud state before pushing empty
            try {
              const cloudRecords = await cloudClient.pull(collection, item.vesselId, 0);
              const cloudHasData = Array.isArray(cloudRecords) && cloudRecords.length > 0;

              if (cloudHasData) {
                devLog(`[OUTBOX] ⛔ BLOCKED empty push for ${collection} — cloud has ${cloudRecords.length} records`);
                // Trigger force-pull to restore local data
                window.dispatchEvent(new CustomEvent('sr:sync:force-pull', {
                  detail: { collection, vesselId: item.vesselId, reason: 'outbox-empty-push-blocked' }
                }));
                await markOutboxFailed(item.id, 'Empty push blocked - cloud has data', { kind: FAILURE_KIND_BLOCKED_EMPTY });
                failedCount++;
                continue; // Skip this item
              }
              devLog(`[OUTBOX] ✅ Empty push allowed for ${collection} — cloud is also empty`);
            } catch (pullError) {
              devLog(`[OUTBOX] ⛔ BLOCKED empty push for ${collection} — cloud check failed:`, pullError);
              await markOutboxFailed(item.id, 'Empty push blocked - cloud check failed', { kind: FAILURE_KIND_BLOCKED_EMPTY });
              failedCount++;
              continue;
            }
          }

          // 🛡️ PULL-MERGE-PUSH PATTERN (2026-03-07): Prevent partial pushes from wiping cloud data
          // For ARRAY_COLLECTIONS, always merge local with cloud before pushing
          // FIX 2026-03-08: UNWRAP cloud records before merging (prevents triple-nesting)
          // FIX 2026-03-09: RECURSIVE unwrap to handle any depth of nesting (cloud has 4+ levels)
          let finalSyncData = syncData;
          if (ARRAY_COLLECTIONS.has(collection) && syncData._isArray && Array.isArray(syncData.items)) {
            const rawLocalItems = syncData.items;

            // 🛡️ FIX 2026-03-09: Log BEFORE pull-merge-push
            console.log(`[OUTBOX] PUSHING: ${collection}, items: ${rawLocalItems?.length ?? 'no items array'}`);

            try {
              // 1. PULL: Get current cloud data
              const cloudRecords = await cloudClient.pull(collection, item.vesselId, 0);
              console.log(`[OUTBOX] PULLED: ${collection}, cloud records: ${cloudRecords?.length ?? 0}`);

              // 2. RECURSIVE UNWRAP: Extract individual items from any depth of nesting
              // Cloud may have: [{id: "vessel_crew", data: {items: [{id: "vessel_crew", data: {items: [...]}}]}}]
              // We need: [{id: "CRW-xxx", name: "John"}, ...]
              //
              // FT-331 amend (2026-07-07): the parallel inline `extractItems` that
              // used to live here was DELETED and replaced with an import of the
              // shared, hoisted implementation from `src/sync/extractItems.js`.
              // The inline copy shared the FT-324 ghost-doc-poisoning bug class
              // (wrapper items with `data._sourceModule` slipping through into the
              // pushed doc) and was NOT patched by the initial FT-331 commit.
              // Both this module and UniversalSyncBridge now funnel through the
              // same wrapper-rejection guard.

              // 🛡️ FIX 2026-03-09: Unwrap BOTH cloud AND local items
              const cloudItems = extractItems(cloudRecords);
              const localItems = extractItems(rawLocalItems);

              console.log(`[OUTBOX] EXTRACTED: ${cloudItems.length} cloud items, ${localItems.length} local items`);

              // 3. MERGE: Union of cloud + local (local wins for conflicts)
              const uniqueMap = new Map();

              // Add cloud items first
              cloudItems.forEach(i => {
                if (i && i.id) uniqueMap.set(i.id, i);
              });

              // Add local items — FT-446 (2026-08-15) SAFETY-CRITICAL: was an
              // UNCONDITIONAL overwrite (`uniqueMap.set(i.id, i)` for every
              // local item, NO timestamp check) so a stale queued local item
              // clobbered a NEWER cloud copy — the cross-device sign-off
              // clobber, on the offline-drain path. Now uses the SAME safe
              // tie-break as the bridge push merge: LOCAL wins ONLY when its
              // ts is known and STRICTLY NEWER than cloud (blank/equal/older →
              // CLOUD). A genuinely newer offline edit still wins and pushes.
              localItems.forEach(i => {
                if (!i || !i.id) return;
                const existing = uniqueMap.get(i.id);
                if (!existing) { uniqueMap.set(i.id, i); return; }
                const { localWins } = resolvePushMergeWinner(i, existing);
                if (localWins) { uniqueMap.set(i.id, i); }
                // else: cloud is same-or-newer / local blank — keep cloud (no action)
                // FT-565: combine configured list fields (equipment photos /
                // auditLog) from the LOSING copy into the winner — mirrors the
                // bridge push merge (`mergePushUnion`) so an offline photo add
                // isn't discarded on drain when a newer edit to the same item
                // is already in cloud. Additive only; no-op for unconfigured
                // collections.
                uniqueMap.set(i.id, mergeListFields(collection, uniqueMap.get(i.id), localWins ? existing : i));
                // FT-458: sticky tombstone retain onto the chosen winner (no-op
                // when local isn't tombstoned). Mirrors the bridge push merge so
                // a locally-deleted item doesn't resurrect on the offline-drain.
                uniqueMap.set(i.id, retainTombstone(i, uniqueMap.get(i.id)));
              });

              const mergedItems = [...uniqueMap.values()];

              console.log(`[OUTBOX] MERGED: ${mergedItems.length} total items (cloud: ${cloudItems.length}, local: ${localItems.length})`);

              // 4. PREPARE: Update finalSyncData with merged items
              finalSyncData = { ...syncData, items: mergedItems };

            } catch (pullError) {
              // FAIL CLOSED: If we can't merge with cloud, BLOCK the push
              // The local data is safe in localStorage and will retry on next sync cycle
              console.error(`[OUTBOX] ⛔ BLOCKED push for ${collection} — pull-merge failed:`, pullError.message);
              await markOutboxFailed(item.id, 'Pull-merge failed - push blocked for safety');
              continue; // BLOCK the push — do NOT fall through
            }
          }

          // Push to cloud
          let result = await cloudClient.push(
            collection,
            item.recordId || `${item.vesselId}_${collection}`,
            finalSyncData
          );

          // ============================================================
          // FT-469: OFFLINE-NEW-BOAT SELF-HEAL (preserves FT-49) — vessel-scoped only
          // ============================================================
          // The server no longer blind-auto-creates unknown vesselIds on push
          // (that allowed cross-org contamination). So a genuinely-new vessel
          // created OFFLINE — one that has never been registered server-side —
          // now gets a 403 on its first push. To keep that legitimate case
          // working, on a failed vessel-scoped push we attempt a ONE-TIME
          // register-offline under the CALLER'S OWN org (org derived from JWT),
          // then retry the push exactly once.
          //
          // Safety: register-offline is idempotent and can only ever create the
          // vessel in the caller's OWN org. If the vesselId belongs to ANOTHER
          // org, the server returns 409 and we do NOT retry — the push stays
          // failed (403), which is the correct cross-org rejection.
          //
          // Org-scoped collections (vesselId === orgId sentinel) are excluded:
          // they never hit the vessel-ownership guard, so they never need this.
          const isOrgScopedItem = item.vesselId && item.vesselId === finalSyncData?.__orgScoped;
          if (
            !result.success &&
            item.vesselId &&
            !isOrgScopedItem &&
            /^[0-9a-f-]{36}$/i.test(item.vesselId) &&      // looks like a vessel UUID, not an org sentinel
            !item.__ft469RegisterAttempted                 // guard: only ONE self-heal attempt
          ) {
            try {
              item.__ft469RegisterAttempted = true;
              await azureApiClient.registerOfflineVessel(item.vesselId);
              devLog(`[OUTBOX] FT-469: registered offline vessel ${item.vesselId}, retrying push`);
              result = await cloudClient.push(
                collection,
                item.recordId || `${item.vesselId}_${collection}`,
                finalSyncData
              );
            } catch (regErr) {
              // 409 (foreign-org id collision) or any register failure: leave the
              // original push result as-is → item stays failed (correct for a
              // foreign vessel). Do not throw here.
              devLog(`[OUTBOX] FT-469: register-offline for ${item.vesselId} did not succeed:`, regErr && regErr.message);
            }
          }

          if (result.success) {
            successCount++;
            successIds.push(item.id);
            devLog(`[OUTBOX] Successfully synced ${item.id} to ${collection}`);
          } else {
            // FT-577: keep the HTTP status (e.g. 413) so it is recorded on the
            // entry instead of being flattened into a bare message.
            const pushErr = new Error(result.error || 'Push failed');
            if (typeof result.status === 'number') pushErr.status = result.status;
            throw pushErr;
          }

        } catch (error) {
          devLog(`[OUTBOX] Failed to sync ${item.id}:`, error);
          await markOutboxFailed(item.id, error.message, { status: error.status });
          failedCount++;
          if (collection === 'sr.photos.v1') {
            // FT-577: visible in production (devLog is a no-op there).
            // eslint-disable-next-line no-console -- FT-577 prod observability
            console.warn('[FT-577] photo upload failed', {
              photoId: item.recordId,
              status: error.status ?? null,
              error: error.message,
              attempt: (item.retryCount || 0) + 1,
              maxAttempts: MAX_OUTBOX_RETRIES,
            });
          }
        }
      }
    }

    // Clear successfully synced items
    if (successIds.length > 0) {
      await clearOutboxItems(successIds);
      devLog(`[OUTBOX] Cleared ${successIds.length} synced items`);
    }

    // Emit completion event
    window.dispatchEvent(new CustomEvent('outboxProcessed', {
      detail: { success: successCount, failed: failedCount }
    }));

    return { success: successCount, failed: failedCount, offline: false };

  } catch (error) {
    devLog('[OUTBOX] Critical error during processing:', error);
    return { success: 0, failed: 0, error: error.message };
  }
};

/**
 * Get current vessel ID (helper)
 * @param {string} vesselId - The vessel ID passed from React component
 * @returns {string} The vessel ID to use
 */
const getCurrentVesselId = async (vesselId) => {
  // Use passed vesselId if available
  if (vesselId) return vesselId;

  // Fallback to first vessel
  const vessels = await readJSON('vessels', []);
  return vessels[0]?.id || 'default';
};

/**
 * FT-269: read-only helper for the voyage event audit-context merge path.
 *
 * Scans the outbox for the most recent pending voyage-collection entry that
 * references the given (voyageId, eventId) pair and returns
 * { retryCount, queuedAt } when found. Returns null when no matching entry
 * exists (either nothing queued, or the entry has already drained).
 *
 * NO writes. NO change to processOutbox / push semantics. This helper is
 * additive per brief §22 — touching push/process logic is forbidden.
 *
 * @param {string} voyageId
 * @param {string} eventId
 * @returns {Promise<{ retryCount: number, queuedAt: string|number } | null>}
 */
export const getOutboxEntryForVoyageEvent = async (voyageId, eventId) => {
  if (!voyageId || !eventId) return null;
  try {
    const outbox = await listOutbox();
    if (!Array.isArray(outbox) || outbox.length === 0) return null;

    // Filter to voyage-collection entries; iterate newest-first so we
    // surface the latest queued attempt.
    const candidates = outbox
      .filter(e => e?.collection === 'sr.operations_voyages.v1')
      .sort((a, b) => {
        const at = new Date(a.timestamp || 0).getTime();
        const bt = new Date(b.timestamp || 0).getTime();
        return bt - at;
      });

    for (const entry of candidates) {
      const items = Array.isArray(entry?.data?.items) ? entry.data.items : [];
      const voyage = items.find(v => v?.id === voyageId);
      if (!voyage) continue;
      const events = [
        ...(Array.isArray(voyage.unifiedLog) ? voyage.unifiedLog : []),
        ...(Array.isArray(voyage.logEntries) ? voyage.logEntries : [])
      ];
      if (events.some(e => e?.id === eventId)) {
        return {
          retryCount: typeof entry.retryCount === 'number' ? entry.retryCount : 0,
          queuedAt: entry.timestamp || null
        };
      }
    }
    return null;
  } catch (err) {
    devLog('[outbox] FT-269 getOutboxEntryForVoyageEvent failed (non-fatal):', err);
    return null;
  }
};

/**
 * Get outbox statistics
 */
export const getOutboxStats = async () => {
  const outbox = await listOutbox();

  return {
    total: outbox.length,
    pending: outbox.filter(i => i.status === 'pending').length,
    failed: outbox.filter(i => i.status === 'failed').length,
    byCollection: outbox.reduce((acc, item) => {
      acc[item.collection] = (acc[item.collection] || 0) + 1;
      return acc;
    }, {}),
    oldestPending: outbox
      .filter(i => i.status === 'pending')
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))[0]?.timestamp
  };
};